import useAddressForm from "components/shared/address/controller";
import { useManagementTextField } from "hooks/forms/ManagementHooks";
import { useLoadingToast } from "hooks/toast/toasts";
import { toast } from "react-toastify";
import { useState, useRef, useCallback } from "react";
import useAttorneyForm from "../attorney/controller";
import { generateAttorneyPdf, getProject, getProjectById, getProjectsByName } from "services/api/api";
import { downloadFileFromApi } from "services/pdf/download-file-from-api";

export const useEditProject = () => {
  const addressController = useAddressForm();
  const attorneyController = useAttorneyForm();

  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();
  const { reset: resetProjectNumber, ...projectNumber } = useManagementTextField("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [projectData, setProjectData] = useState();
  const [disableAllFields, setDisableAllFields] = useState(false);
  const [clientValue, setClientValue] = useState("");

  const [searchByName, setSearchByName] = useState(false);
  const changeSearch = () => {
    setSearchByName((prev) => !prev);
  };

  const [possibleProjects, setPossibleProjects] = useState([]);
  const searchTimeoutRef = useRef(null);

  const handleOnSearch = useCallback((string) => {
    // Limpar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (string === "" || !string || string.length < 2) {
      setPossibleProjects([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    
    // Debounce de 500ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setProjectData(null);
        const { data } = await getProjectsByName(string);
        setPossibleProjects(data || []);
      } catch (error) {
        console.error("Erro ao buscar projetos:", error);
        setPossibleProjects([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  }, []);

  const handleOnSelect = (item) => {
    setProjectData(item);
  };

  const handleOnClear = () => {
    setProjectData(null);
    setPossibleProjects([]);
  }

  //   const [searchValue, setSearchValue] = useState([]);

  const handleClientChange = async (event, newValue) => {
    setClientValue(newValue);
  };

  const updateProjectAddress = (addressData) => {
    if (projectData) {
      setProjectData({
        ...projectData,
        street: addressData.street,
        number: addressData.number,
        district: addressData.district,
        addressDetails: addressData.addressDetails,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
      });
    }
  };

  const clearAllFields = () => {
    resetProjectNumber();
    setProjectData(null);
  };

  const handleSearchProject = async (event) => {
    event.preventDefault();
    let searchToastId;
    try {
      // Limpar dados do projeto anterior (se houver)
      setProjectData(null);
      setSearchLoading(true);
      searchToastId = initializeLoadingToast({
        text: `Buscando Projeto...`,
      });

      const { data } = await getProject(projectNumber.value);

      setClientValue(data.clients);
      setProjectData(data);
      addressController.loadData({
        street: data.street,
        number: data.number,
        district: data.district,
        addressDetails: data.addressDetails,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      });
      attorneyController.loadData(data);
      updateLoadingToast({
        id: searchToastId,
        text: `Projeto encontrado com sucesso`,
        type: "success",
      });
      return { success: true, data };
    } catch (error) {
      updateLoadingToast({
        id: searchToastId,
        text: error?.response?.data?.message ?? `Erro ao buscar Projeto, tente novamente`,
        type: "error",
      });
      clearAllFields();
      return { success: false, error };
    } finally {
      setSearchLoading(false);
      searchToastId = null;
    }
  };

  // Função para carregar projeto por número (usado para navegação via notificações)
  const loadProjectByNumber = async (projectNumber) => {
    try {
      setSearchLoading(true);

      const { data } = await getProject(projectNumber);

      setClientValue(data.clients);
      setProjectData(data);
      addressController.loadData({
        street: data.street,
        number: data.number,
        district: data.district,
        addressDetails: data.addressDetails,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      });
      attorneyController.loadData(data);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? `Erro ao carregar Projeto, tente novamente`
      );
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para carregar projeto por ID (usado para navegação via notificações)
  const loadProjectById = async (projectId, silent = false) => {
    try {
      if (!silent) {
        setSearchLoading(true);
      }

      const { data } = await getProjectById(projectId);

      setClientValue(data.clients);
      setProjectData(data);
      addressController.loadData({
        street: data.street,
        number: data.number,
        district: data.district,
        addressDetails: data.addressDetails,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      });
      attorneyController.loadData(data);
    } catch (error) {
      if (!silent) {
        toast.error(
          error?.response?.data?.message ?? `Erro ao carregar Projeto, tente novamente`
        );
      }
    } finally {
      if (!silent) {
        setSearchLoading(false);
      }
    }
  };

  const client = {
    value: clientValue,
    onChange: handleClientChange,
  };

  const [isAttorneySending, setIsAttorneySending] = useState(false);

  const handleSendAttorney = async (event) => {
    event.stopPropagation();
    let sendAttorneyToastId;
    setIsAttorneySending(true);
    setDisableAllFields(true);
    try {
      sendAttorneyToastId = initializeLoadingToast({
        text: `Gerando Procuração...`,
      });

      const fileName = `Procuração - ${projectData.projectNumber} - ${projectData.name}.pdf`;
      const { status, data } = await generateAttorneyPdf(projectData.id);

      if (status === 200) {
        updateLoadingToast({
          id: sendAttorneyToastId,
          text: `Procuração gerada com sucesso`,
          type: "success",
        });

        downloadFileFromApi(data, fileName);
      }
    } catch (error) {
      updateLoadingToast({
        id: sendAttorneyToastId,
        text: error.message ?? `Erro ao gerar procuração, tente novamente`,
        type: "error",
      });
    } finally {
      setIsAttorneySending(false);
      setDisableAllFields(false);
    }
  };

  const voidFunction = () => {};

  return {
    handleSearchProject,
    loadProjectByNumber,
    loadProjectById,
    clearAllFields,
    updateProjectAddress,
    setDisableAllFields,
    initializeLoadingToast,
    updateLoadingToast,
    handleSendAttorney,
    disableAllFields,
    projectNumber,
    resetProjectNumber,
    client,
    searchLoading,
    projectData,
    setProjectData,
    addressController,
    attorneyController,
    isAttorneySending,

    searchByName,
    changeSearch,
    voidFunction,
    handleOnSearch,
    handleOnSelect,
    handleOnClear,
    possibleProjects,
  };
};
